import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';

import { useAppSelector } from '../../hooks';
import { TApplicationSourceList, TApplicationSourceListResponse, TApplicationSourceQuery, adaptApplicationListSourceQueryToRequest, adaptApplicationSourceFromListResponse } from '../../types';
import { BACKEND_SOURCE_APPLICATION } from '../../backend-paths';


export const sourceApplicationGetApi = createAsyncThunk(
	BACKEND_SOURCE_APPLICATION,
	(payload: TApplicationSourceQuery | undefined, thunkApi) => {
		const params = adaptApplicationListSourceQueryToRequest(payload);

		return HttpService.get<TApplicationSourceListResponse>(BACKEND_SOURCE_APPLICATION, { params }).then(result => {
			return adaptApplicationSourceFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TApplicationSourceList;
	query: TApplicationSourceQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 50,
		offset: 0,
		search: undefined,
	},
	error: undefined,
};

export const sourceApplicationSlice = createSlice({
	name: 'sourceApplicationSlice',
	initialState,
	reducers: {
		setSourceApplicationQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		clearSourceApplication(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[sourceApplicationGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[sourceApplicationGetApi.fulfilled.type]: (state, action: PayloadAction<TApplicationSourceList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[sourceApplicationGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useSourceApplicationSelector = () => useAppSelector(state => state[sourceApplicationSlice.name]);

export const { clearSourceApplication, setSourceApplicationQuerySearch } = sourceApplicationSlice.actions;
